import { BusinessActivity } from '@/types/businessAppTypes';
import { Badge } from '@/components/ui/badge';
import { Edit2, Trash2, DollarSign, Users, Tag, Image } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

// Import the reusable DataTable component
import { DataTable, type TableColumn, type TableAction } from '../../shared/DataTable';

interface ActivityTableProps {
  activities: BusinessActivity[];
  onEdit: (activity: BusinessActivity) => void;
  onDelete: (id: string) => void;
}

export function ActivityTable({ activities, onEdit, onDelete }: ActivityTableProps) {
  // Define table columns
  const columns: TableColumn<BusinessActivity>[] = [
    {
      key: 'name',
      header: 'Name',
      accessor: (item) => (
        <div className="font-medium text-gray-900">
          {item.name}
        </div>
      ),
      sortable: true
    },
    {
      key: 'activityType',
      header: 'Type',
      accessor: (item) => (
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-gray-400" />
          <Badge variant="outline" className="capitalize">
            {item.activityType}
          </Badge>
        </div>
      ),
      sortable: true
    },
    {
      key: 'pricing',
      header: 'Price (Adult)',
      accessor: (item) => (
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-gray-400" />
          <div className="text-sm font-medium">
            {formatCurrency(
              item.pricing?.defaultPrices?.adult?.amount || 0, 
              item.pricing?.defaultPrices?.adult?.currency || 'USD'
            )}
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'capacity',
      header: 'Capacity',
      accessor: (item) => (
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-400" />
          <div className="text-sm font-medium">
            {item.maxParticipants}
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'image',
      header: 'Image',
      accessor: (item) => (
        <div className="flex items-center gap-2">
          {item.images && item.images.length > 0 ? (
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 rounded overflow-hidden border border-slate-200">
                <img src={item.images[0]} alt={item.name} className="w-full h-full object-cover" />
              </div>
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 text-xs">
                Set
              </Badge>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center border border-slate-200">
                <Image className="w-4 h-4 text-slate-400" />
              </div>
              <Badge variant="outline" className="text-xs text-slate-500">
                Default
              </Badge>
            </div>
          )}
        </div>
      ),
      sortable: false,
      width: '120px'
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (item) => (
        <Badge
          variant={item.isActive ? "default" : "secondary"}
          className={item.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}
        >
          {item.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
      sortable: true,
      width: '120px'
    }
  ];

  // Define table actions
  const actions: TableAction<BusinessActivity>[] = [
    {
      key: 'edit',
      label: 'Edit Activity',
      icon: Edit2,
      onClick: onEdit
    },
    {
      key: 'delete',
      label: 'Delete Activity',
      icon: Trash2,
      variant: 'destructive',
      onClick: (item) => onDelete(item._id)
    }
  ];

  return (
    <DataTable
      data={activities}
      columns={columns}
      actions={actions}
      searchable={true}
      searchPlaceholder="Search by activity name, type..."
      searchKeys={['name', 'activityType']}
      filterable={true}
      filters={[
        {
          key: 'isActive',
          label: 'Status',
          options: [
            { value: 'true', label: 'Active' },
            { value: 'false', label: 'Inactive' }
          ]
        },
        {
          key: 'activityType',
          label: 'Type',
          options: [
            { value: 'observation', label: 'Observation' },
            { value: 'photography', label: 'Photography' },
            { value: 'research', label: 'Research' },
            { value: 'feeding', label: 'Feeding' },
            { value: 'tour', label: 'Tour' },
            { value: 'educational', label: 'Educational' }
          ]
        }
      ]}
      emptyMessage="No activities found. Create your first activity to get started."
    />
  );
} 